// SEO Landing Pages Data for Location-based Services
// Targeting: Mumbai, Pune, Singapore, USA, Australia
// Services: GMB SEO, Website SEO, SMM, Performance Marketing

export const locationServices = {
  // Service definitions
  services: {
    'gmb-seo': {
      name: 'Google My Business SEO',
      shortName: 'GMB SEO',
      description: 'Dominate local search results with our expert Google My Business optimization services.',
      icon: 'MapPin',
      benefits: [
        'Rank #1 in Google Maps & Local Pack',
        'Increase walk-in traffic & phone calls',
        'Build trust with verified reviews',
        'Optimize for "near me" searches',
        'Local citation building',
        'NAP consistency management'
      ],
      process: [
        { title: 'GMB Audit', desc: 'Complete analysis of your current GMB profile and local presence' },
        { title: 'Optimization', desc: 'Optimize all GMB fields, categories, and attributes' },
        { title: 'Content Strategy', desc: 'Regular posts, offers, and Q&A management' },
        { title: 'Review Management', desc: 'Generate and respond to customer reviews' }
      ],
      faqs: [
        { q: 'How long does GMB SEO take to show results?', a: 'Most businesses see improved local rankings within 4-8 weeks. Full optimization benefits typically appear within 3 months.' },
        { q: 'Do you guarantee Google Maps rankings?', a: 'While we cannot guarantee specific rankings, our proven strategies consistently deliver top 3 local pack positions for our clients.' },
        { q: 'What industries do you serve for GMB SEO?', a: 'We specialize in local businesses including healthcare, restaurants, retail, professional services, and home services.' }
      ]
    },
    'website-seo': {
      name: 'Website SEO Services',
      shortName: 'SEO Services',
      description: 'Drive organic traffic and qualified leads with comprehensive website SEO strategies.',
      icon: 'Search',
      benefits: [
        'First page Google rankings',
        'Increased organic traffic',
        'Higher quality leads',
        'Reduced ad dependency',
        'Long-term sustainable growth',
        'Competitive advantage'
      ],
      process: [
        { title: 'Technical Audit', desc: 'Comprehensive website health check and technical optimization' },
        { title: 'Keyword Research', desc: 'Identify high-value keywords your customers are searching' },
        { title: 'On-Page SEO', desc: 'Optimize content, meta tags, and internal linking' },
        { title: 'Link Building', desc: 'Build authority with quality backlinks' }
      ],
      faqs: [
        { q: 'How long until I see SEO results?', a: 'SEO is a long-term strategy. Initial improvements appear in 3-4 months, with significant results in 6-12 months.' },
        { q: 'What makes your SEO different?', a: 'We focus on revenue, not just rankings. Our data-driven approach delivers measurable ROI, not vanity metrics.' },
        { q: 'Do you follow Google guidelines?', a: 'Absolutely. We use only white-hat SEO techniques that comply with Google\'s webmaster guidelines.' }
      ]
    },
    'social-media-marketing': {
      name: 'Social Media Marketing',
      shortName: 'SMM Services',
      description: 'Build your brand and engage customers with strategic social media marketing.',
      icon: 'Share2',
      benefits: [
        'Increased brand awareness',
        'Higher engagement rates',
        'Direct customer communication',
        'Lead generation from social',
        'Community building',
        'Influencer partnerships'
      ],
      process: [
        { title: 'Strategy Development', desc: 'Custom social media strategy aligned with your business goals' },
        { title: 'Content Creation', desc: 'Engaging posts, graphics, and videos for your audience' },
        { title: 'Community Management', desc: 'Active engagement and response management' },
        { title: 'Analytics & Reporting', desc: 'Track performance and optimize for better results' }
      ],
      faqs: [
        { q: 'Which social media platforms should I use?', a: 'It depends on your target audience. We help identify the platforms where your customers are most active.' },
        { q: 'How often should I post on social media?', a: 'Consistency matters more than frequency. We recommend 3-5 quality posts per week for most businesses.' },
        { q: 'Can social media generate leads?', a: 'Yes! With the right strategy, social media is a powerful lead generation channel, especially for B2B and local businesses.' }
      ]
    },
    'performance-marketing': {
      name: 'Performance Marketing',
      shortName: 'PPC & Ads',
      description: 'ROI-focused paid advertising campaigns that deliver measurable results.',
      icon: 'TrendingUp',
      benefits: [
        'Immediate visibility',
        'Precise audience targeting',
        'Measurable ROI',
        'Scalable campaigns',
        'A/B testing optimization',
        'Multi-platform expertise'
      ],
      process: [
        { title: 'Campaign Strategy', desc: 'Define goals, audience, and budget allocation' },
        { title: 'Ad Creation', desc: 'Compelling ad copy and creative development' },
        { title: 'Launch & Monitor', desc: 'Campaign deployment and real-time monitoring' },
        { title: 'Optimize & Scale', desc: 'Continuous optimization to maximize ROI' }
      ],
      faqs: [
        { q: 'What platforms do you advertise on?', a: 'We manage campaigns on Google Ads, Meta (Facebook/Instagram), LinkedIn, and other platforms based on your audience.' },
        { q: 'What is a good ROAS to expect?', a: 'This varies by industry, but our clients typically see 3-10x return on ad spend after optimization.' },
        { q: 'How much should I budget for ads?', a: 'We recommend starting with a test budget of $1,000-3,000/month to gather data, then scaling what works.' }
      ]
    }
  },

  // Location definitions
  locations: {
    'mumbai': {
      name: 'Mumbai',
      country: 'India',
      region: 'Maharashtra',
      currency: 'INR',
      phone: '+91 98679 58759',
      timezone: 'IST',
      localKeywords: ['Mumbai', 'Bombay', 'Maharashtra', 'Western India'],
      businessAreas: ['Andheri', 'Bandra', 'Lower Parel', 'BKC', 'Powai', 'Thane', 'Navi Mumbai'],
      marketInsight: 'As India\'s financial capital, Mumbai has a highly competitive digital market. Businesses need strong online presence to stand out.',
      stats: { businesses: '2M+', internetUsers: '15M+', searchVolume: 'High' }
    },
    'pune': {
      name: 'Pune',
      country: 'India',
      region: 'Maharashtra',
      currency: 'INR',
      phone: '+91 98679 58759',
      timezone: 'IST',
      localKeywords: ['Pune', 'Poona', 'Maharashtra', 'IT Hub'],
      businessAreas: ['Hinjewadi', 'Kharadi', 'Wakad', 'Koregaon Park', 'Viman Nagar', 'Baner'],
      marketInsight: 'Pune\'s growing IT sector and startup ecosystem creates massive demand for digital marketing services.',
      stats: { businesses: '500K+', internetUsers: '6M+', searchVolume: 'High' }
    },
    'singapore': {
      name: 'Singapore',
      country: 'Singapore',
      region: 'Southeast Asia',
      currency: 'SGD',
      phone: '+65 XXXX XXXX',
      timezone: 'SGT',
      localKeywords: ['Singapore', 'SG', 'Southeast Asia', 'APAC'],
      businessAreas: ['CBD', 'Orchard', 'Marina Bay', 'Raffles Place', 'Changi'],
      marketInsight: 'Singapore is a digital-first market with high smartphone penetration and sophisticated consumers.',
      stats: { businesses: '300K+', internetUsers: '5M+', searchVolume: 'Very High' }
    },
    'usa': {
      name: 'United States',
      country: 'USA',
      region: 'North America',
      currency: 'USD',
      phone: '+1 XXX XXX XXXX',
      timezone: 'Multiple',
      localKeywords: ['USA', 'United States', 'America', 'US'],
      businessAreas: ['New York', 'Los Angeles', 'Chicago', 'Houston', 'San Francisco', 'Miami'],
      marketInsight: 'The US market is highly competitive but offers massive opportunities for businesses with strong digital presence.',
      stats: { businesses: '33M+', internetUsers: '310M+', searchVolume: 'Highest' }
    },
    'australia': {
      name: 'Australia',
      country: 'Australia',
      region: 'Oceania',
      currency: 'AUD',
      phone: '+61 X XXXX XXXX',
      timezone: 'Multiple',
      localKeywords: ['Australia', 'AU', 'Oceania', 'Down Under'],
      businessAreas: ['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide', 'Gold Coast'],
      marketInsight: 'Australia has high digital adoption rates and consumers who actively search for local services online.',
      stats: { businesses: '2.5M+', internetUsers: '23M+', searchVolume: 'High' }
    }
  },

  // Generate all landing page combinations
  getPageData: function(serviceSlug, locationSlug) {
    const service = this.services[serviceSlug];
    const location = this.locations[locationSlug];
    
    if (!service || !location) return null;

    const pageTitle = `${service.shortName} in ${location.name}`;
    const fullTitle = `${service.name} in ${location.name} | Digital Startup`;
    const slug = `${serviceSlug}-${locationSlug}`;

    return {
      slug,
      serviceSlug,
      locationSlug,
      pageTitle,
      fullTitle,
      service,
      location,
      metaDescription: `Expert ${service.shortName} services in ${location.name}. ${service.description} Get a free consultation today!`,
      h1: `${service.name} in ${location.name}`,
      heroSubtitle: `Grow your ${location.name} business with our proven ${service.shortName.toLowerCase()} strategies. We help local businesses dominate online.`,
      ctaText: `Get Free ${service.shortName} Audit`,
      testimonial: {
        text: `Digital Startup transformed our online presence in ${location.name}. Our leads increased by 250% in just 3 months!`,
        author: 'Satisfied Client',
        company: `${location.name} Business Owner`
      }
    };
  },

  // Get all page slugs for routing
  getAllPageSlugs: function() {
    const slugs = [];
    Object.keys(this.services).forEach(serviceSlug => {
      Object.keys(this.locations).forEach(locationSlug => {
        slugs.push(`${serviceSlug}-${locationSlug}`);
      });
    });
    return slugs;
  }
};

// Export individual arrays for easy access
export const allLocationPages = [
  // GMB SEO Pages
  { slug: 'gmb-seo-mumbai', service: 'gmb-seo', location: 'mumbai', title: 'GMB SEO Services in Mumbai' },
  { slug: 'gmb-seo-pune', service: 'gmb-seo', location: 'pune', title: 'GMB SEO Services in Pune' },
  { slug: 'gmb-seo-singapore', service: 'gmb-seo', location: 'singapore', title: 'GMB SEO Services in Singapore' },
  { slug: 'gmb-seo-usa', service: 'gmb-seo', location: 'usa', title: 'GMB SEO Services in USA' },
  { slug: 'gmb-seo-australia', service: 'gmb-seo', location: 'australia', title: 'GMB SEO Services in Australia' },
  
  // Website SEO Pages
  { slug: 'website-seo-mumbai', service: 'website-seo', location: 'mumbai', title: 'SEO Services in Mumbai' },
  { slug: 'website-seo-pune', service: 'website-seo', location: 'pune', title: 'SEO Services in Pune' },
  { slug: 'website-seo-singapore', service: 'website-seo', location: 'singapore', title: 'SEO Services in Singapore' },
  { slug: 'website-seo-usa', service: 'website-seo', location: 'usa', title: 'SEO Services in USA' },
  { slug: 'website-seo-australia', service: 'website-seo', location: 'australia', title: 'SEO Services in Australia' },
  
  // Social Media Marketing Pages
  { slug: 'social-media-marketing-mumbai', service: 'social-media-marketing', location: 'mumbai', title: 'Social Media Marketing in Mumbai' },
  { slug: 'social-media-marketing-pune', service: 'social-media-marketing', location: 'pune', title: 'Social Media Marketing in Pune' },
  { slug: 'social-media-marketing-singapore', service: 'social-media-marketing', location: 'singapore', title: 'Social Media Marketing in Singapore' },
  { slug: 'social-media-marketing-usa', service: 'social-media-marketing', location: 'usa', title: 'Social Media Marketing in USA' },
  { slug: 'social-media-marketing-australia', service: 'social-media-marketing', location: 'australia', title: 'Social Media Marketing in Australia' },
  
  // Performance Marketing Pages
  { slug: 'performance-marketing-mumbai', service: 'performance-marketing', location: 'mumbai', title: 'Performance Marketing in Mumbai' },
  { slug: 'performance-marketing-pune', service: 'performance-marketing', location: 'pune', title: 'Performance Marketing in Pune' },
  { slug: 'performance-marketing-singapore', service: 'performance-marketing', location: 'singapore', title: 'Performance Marketing in Singapore' },
  { slug: 'performance-marketing-usa', service: 'performance-marketing', location: 'usa', title: 'Performance Marketing in USA' },
  { slug: 'performance-marketing-australia', service: 'performance-marketing', location: 'australia', title: 'Performance Marketing in Australia' },
];

export default locationServices;
